import React, { useRef } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTranslation } from "react-i18next";

interface TranscriptFieldProps {
	readonly label: string;
	readonly value: string;
	readonly onChange: (value: string) => void;
	readonly required?: boolean;
	readonly error?: string;
	readonly onFileError: (messageKey: string) => void;
}

const isTextFile = (file: File) => file.type === "text/plain" || file.name.toLowerCase().endsWith(".txt");

const TranscriptField: React.FC<TranscriptFieldProps> = ({ label, value, onChange, required, error, onFileError }) => {
	const { t } = useTranslation();
	const fileInput = useRef<HTMLInputElement>(null);

	const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) {
			return;
		}

		if (!isTextFile(file)) {
			onFileError("presentationDraft.start.errors.fileType");
			return;
		}

		const reader = new FileReader();
		reader.onload = () => onChange(typeof reader.result === "string" ? reader.result : "");
		reader.onerror = () => onFileError("presentationDraft.start.errors.fileRead");
		reader.readAsText(file, "UTF-8");
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<TextField
				label={label}
				required={required}
				value={value}
				onChange={event => onChange(event.target.value)}
				placeholder={t("presentationDraft.start.transcriptPlaceholder")}
				multiline
				minRows={6}
				maxRows={12}
				fullWidth
				error={!!error}
				helperText={error}
				slotProps={{ inputLabel: { shrink: true } }}
			/>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
				<Button size="small" startIcon={<UploadFileIcon />} onClick={() => fileInput.current?.click()} sx={{ height: "auto" }}>
					{t("presentationDraft.start.importFile")}
				</Button>
				<Typography variant="caption" color="text.secondary">
					{t("presentationDraft.start.characterCount", { count: value.trim().length })}
				</Typography>
			</Box>
			<input ref={fileInput} type="file" accept=".txt,text/plain" hidden onChange={handleFile} data-testid="transcript-file-input" />
		</Box>
	);
};

export default TranscriptField;
